<script setup lang="ts">
import { computed, onMounted, ref, watch, nextTick } from 'vue'
import { Select, createListCollection } from '@ark-ui/vue'
import UiFieldContainer from './UiFieldContainer.vue'
import UiIcon from './UiIcon.vue'
import UiTooltip from './UiTooltip.vue'

export interface UiSelectOption {
    label: string
    value: string
}

export interface UiSelectProps {
    name?: string
    id?: string
    options: UiSelectOption[]
    placeholder?: string
    disabled?: boolean
    size?: 'md' | 'lg'
    hint?: string
    error?: string
    invalid?: boolean
    multiple?: boolean
}

const props = withDefaults(defineProps<UiSelectProps>(), {
    options: () => [],
    size: 'md',
    multiple: false,
})

const model = defineModel<string[] | string>()

const emit = defineEmits<{
    (e: 'validate'): void
}>()

const selectedValue = ref<string[]>([]);

const describedByBase = computed(() => props.id || props.name)

const collection = computed(() => createListCollection({
    items: props.options,
    itemToValue: (item: UiSelectOption) => item.value,
    itemToString: (item: UiSelectOption) => item.label,
}))

const isOpen = ref<boolean>()

/** Full text of the selection, for the trigger tooltip when the label is truncated. */
const selectedLabel = computed(() => collection.value.items
    .filter((item: UiSelectOption) => selectedValue.value.includes(item.value))
    .map((item: UiSelectOption) => item.label)
    .join(', '))

// Historically UiSelect emitted `validate` on mount (via an `immediate` watcher).
// Keep that behavior without mutating the external v-model, so form consumers
// that listen to `validate` still get an init signal.
onMounted(() => {
    emit('validate')
})

watch(selectedValue, (newVal: string[]) => {
    if (props.multiple && Array.isArray(model.value)) {
        if (model.value.length === newVal.length && model.value.every((item: string, index: number) => item === newVal[index])) return
        model.value = newVal
    } else {
        const next = newVal[0]
        if (model.value === next) return
        model.value = next
    }
    emit('validate')
});

watch(model, (newVal: string[] | string | undefined) => {
    if (props.multiple) {
        const incoming = Array.isArray(newVal) ? newVal : (newVal ? [newVal] : [])
        if (selectedValue.value.length === incoming.length && selectedValue.value.every((item: string, index: number) => item === incoming[index])) return
        selectedValue.value = incoming
    } else {
        const incoming = newVal ? [newVal as string] : []
        if (selectedValue.value[0] === incoming[0] && selectedValue.value.length === incoming.length) return
        selectedValue.value = incoming
    }
}, { immediate: true });

// Force repositioning when dropdown opens to fix SSR positioning issues
// Use requestAnimationFrame after nextTick to ensure lazy-mounted content is rendered
watch(isOpen, (newVal) => {
    if (newVal && typeof window !== 'undefined') {
        nextTick(() => {
            requestAnimationFrame(() => {
                window.dispatchEvent(new Event('resize'))
            })
        })
    }
})
</script>

<template>
    <UiFieldContainer :isFocused="isOpen" :disabled="props.disabled"
        v-bind="{ size: props.size, invalid: props.invalid, hint: props.hint, error: props.error, describedByIdBase: describedByBase }">
        <Select.Root :collection="collection" v-model="selectedValue" :multiple="props.multiple" :disabled="props.disabled"
            :id="props.id || props.name" class="w-full" v-model:open="isOpen" lazyMount>
            <Select.Control>
                <Select.Trigger class="w-full min-w-0 flex items-center justify-between gap-2" :class="props.disabled ? 'cursor-not-allowed text-se-gray-500' : 'cursor-pointer'">
                    <UiTooltip :content="selectedLabel" :disabled="!selectedLabel">
                        <Select.ValueText :placeholder="props.placeholder || 'Select...'" class="min-w-0 flex-1 truncate text-left" />
                    </UiTooltip>
                    <Select.Indicator class="shrink-0">
                        <UiIcon name="chevron-down" class="w-4 h-4" :class="props.disabled ? 'text-se-gray-400' : 'text-se-gray-700'" />
                    </Select.Indicator>
                </Select.Trigger>
            </Select.Control>
            <Teleport to="body" defer>
                <Select.Positioner>
                    <Select.Content class="border border-se-gray-300 rounded-md shadow-md bg-se-white my-2 overflow-x-hidden z-2000 max-h-[min(calc(var(--available-height)-var(--spacing)*4),300px)]" :class="{
                        
                        'w-[calc(var(--reference-width)+var(--spacing)*16)] -ml-8': props.size === 'lg',
                        'w-[calc(var(--reference-width)+var(--spacing)*8)] -ml-4': props.size === 'md',
                    }">
                        <Select.Item v-for="item in collection.items" :key="item.value" :item="item" class="flex items-center justify-between py-2 cursor-pointer hover:bg-se-gray-100" :class="{
                            'px-8': props.size === 'lg',
                            'px-4': props.size === 'md',
                        }">
                            <Select.ItemText>{{ item.label }}</Select.ItemText>
                            <Select.ItemIndicator v-if="props.multiple">
                                <UiIcon name="check" class="w-3 h-3 text-se-gray-700" />
                            </Select.ItemIndicator>
                        </Select.Item>
                    </Select.Content>
                </Select.Positioner>
            </Teleport>
            <Select.HiddenSelect />
        </Select.Root>
    </UiFieldContainer>
</template>
